/**
 * useChangeAnalysis — load + run F7 change-importance + F8 change-summary (T4).
 *
 * GET seeds both the importance list and the summary when the view becomes
 * active. `run()` POSTs both capabilities (sequentially — both materialize the
 * working tree one-shot, so we avoid racing two materializations), then refetches
 * so the view reflects the persisted conclusions.
 *
 * Partial-success (ADR-114 D7): an F7 run can succeed overall while some changes
 * degraded (`failedCount`); an F8 run can degrade to an empty `degraded` summary.
 * Neither aborts the run.
 *
 * Disambiguating the empty state: F8's `summary.generatedAt` is the cross-session
 * truth ("was this comparison ever analyzed"); `ranThisSession` covers the
 * in-session run so fresh degrade info is shown immediately.
 *
 * `onError` is read through a ref so `fetchResults` / `run` keep a stable
 * identity even when the caller passes an inline callback — otherwise the seed
 * effect would re-fire on every render and loop GET forever (F6-6 bug parity).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../lib/transport';
import {
  veridocApi,
  type ChangeSummary,
  type ImportanceResult,
  type ImportanceSummary,
} from '../../lib/veridoc-api';

export type ChangeAnalysisStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseChangeAnalysisOptions {
  comparisonId: string | undefined;
  /** Only fetch while the view is actually active (5th tab selected). */
  enabled: boolean;
  /** lawyer / reviewer may trigger a run; viewer is read-only. */
  canRun: boolean;
  onError: (message: string) => void;
}

export interface UseChangeAnalysisResult {
  status: ChangeAnalysisStatus;
  results: ImportanceResult[];
  summary: ChangeSummary | null;
  /** Summary of the most recent F7 run in this session (null until one runs). */
  lastRun: ImportanceSummary | null;
  running: boolean;
  /** True once a run completed in this session (disambiguates the empty state). */
  ranThisSession: boolean;
  run: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useChangeAnalysis({
  comparisonId,
  enabled,
  canRun,
  onError,
}: UseChangeAnalysisOptions): UseChangeAnalysisResult {
  const [status, setStatus] = useState<ChangeAnalysisStatus>('idle');
  const [results, setResults] = useState<ImportanceResult[]>([]);
  const [summary, setSummary] = useState<ChangeSummary | null>(null);
  const [lastRun, setLastRun] = useState<ImportanceSummary | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [ranThisSession, setRanThisSession] = useState<boolean>(false);

  // Latest onError without making it a dependency of the stable callbacks.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Guards against setting state after the comparison/view changed underneath us.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Re-entrancy guard read synchronously (matches onErrorRef) so `run` keeps a
  // stable identity and a fast double-click can't slip past the `running` check
  // before React commits setRunning(true).
  const runningRef = useRef(false);
  const setRunningState = useCallback((value: boolean): void => {
    runningRef.current = value;
    setRunning(value);
  }, []);

  // `silent` is taken as an options object (not a bare boolean) so an accidental
  // event arg from a click handler can't be coerced truthy — refresh is exposed
  // publicly. The catch path of run() passes silent:true because run() already
  // reported the originating error; a failed recovery GET must not toast twice.
  const fetchResults = useCallback(
    async (options: { silent?: boolean } = {}): Promise<void> => {
      if (!comparisonId) return;
      try {
        const [importance, sum] = await Promise.all([
          veridocApi.getChangeImportance(comparisonId),
          veridocApi.getChangeSummary(comparisonId),
        ]);
        if (!aliveRef.current) return;
        setResults(importance.results);
        setSummary(sum);
        setStatus('ready');
      } catch (err) {
        if (!aliveRef.current) return;
        if (!options.silent) {
          setStatus('error');
          onErrorRef.current(errorMessage(err));
        }
      }
    },
    [comparisonId],
  );

  // Seed once the view is active. Re-seed when the comparison changes.
  useEffect(() => {
    if (!enabled || !comparisonId) return;
    setStatus('loading');
    void fetchResults();
  }, [enabled, comparisonId, fetchResults]);

  const run = useCallback(async (): Promise<void> => {
    if (!comparisonId || !canRun || runningRef.current) return;
    setRunningState(true);
    try {
      // Sequential: both POSTs materialize the working tree (one-shot, frozen);
      // running them in parallel would race the first materialization. Record
      // F7's outcome immediately after it persists so a later F8 failure can't
      // hide the F7 verdicts that already committed server-side.
      const importanceSummary = await veridocApi.runChangeImportance(comparisonId);
      if (!aliveRef.current) return;
      setLastRun(importanceSummary);
      setRanThisSession(true);
      await veridocApi.runChangeSummary(comparisonId);
      if (!aliveRef.current) return;
      await fetchResults();
    } catch (err) {
      if (!aliveRef.current) return;
      onErrorRef.current(errorMessage(err));
      // Surface whatever did persist (e.g. F7 committed, then F8 threw) instead
      // of leaving fresh results invisible with no retry affordance. Silent: the
      // run error is already reported, so a failed recovery GET must not re-toast.
      await fetchResults({ silent: true });
    } finally {
      if (aliveRef.current) setRunningState(false);
    }
  }, [comparisonId, canRun, fetchResults, setRunningState]);

  return {
    status,
    results,
    summary,
    lastRun,
    running,
    ranThisSession,
    run,
    refresh: fetchResults,
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return '当前角色无权发起变更分析。';
    if (err.status === 404) return '比对不存在或无访问权限。';
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
